/**
 * MTE SignIn form — Phase 4 auth UI structure (M-C2.0+ pre-infra).
 *
 * Form email + password + forgot-password link. ZERO fetch / ZERO supabase.
 * Após setup infra: wire ao POST /api/auth/signin (D-AUTH-02 v1.2).
 *
 * Source spec: Atlas d1-schema-supabase-v1.2 §Device-Auth flow + Boussole §3.
 * A11y: aria-invalid, aria-describedby, aria-live regions, focus-visible.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout, Header } from '../../components/mte/shell';
import '../../styles/auth.css';
import { apiFetch, ApiError } from '../../lib/auth-fetch';

export const SignInSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

export type SignInForm = z.infer<typeof SignInSchema>;

interface SignInProps {
  onSubmit?: (data: SignInForm) => Promise<void> | void;
  redirectTo?: string;
}

function SignIn(props: SignInProps = {}): JSX.Element {
  const navigate = useNavigate();
  const [submitState, setSubmitState] = useState<{ status: 'idle' | 'submitting' | 'error'; message: string }>({ status: 'idle', message: '' });

  const {
    register, handleSubmit, formState: { errors, isSubmitting },
  } = useForm<SignInForm>({
    resolver: zodResolver(SignInSchema),
    defaultValues: { email: '', password: '' },
  });

  const onValid = async (data: SignInForm): Promise<void> => {
    setSubmitState({ status: 'submitting', message: '' });
    try {
      const result = await apiFetch<{ userId: string; needsDeviceAuth: boolean }>('/api/auth/signin', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (result.needsDeviceAuth) {
        navigate('/device-authorize/', { replace: true });
      } else {
        navigate(props.redirectTo ?? '/famille/accueil/', { replace: true });
      }
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.code === 'unauth' ? 'Identifiants invalides' : e.message)
        : 'Connexion impossible';
      setSubmitState({ status: 'error', message: msg });
    }
  };

  return (
    <Layout context="adulte" width="narrow" header={<Header context="adulte" />}>
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-h1">Se connecter</h1>
          {submitState.status === 'error' && (
            <div className="auth-error-region" role="alert" aria-live="assertive" data-testid="signin-error-region">
              {submitState.message}
            </div>
          )}
          <form className="auth-form" onSubmit={handleSubmit(onValid)} noValidate data-testid="signin-form">
            <div className="auth-field">
              <label htmlFor="email" className="auth-label">Email</label>
              <input id="email" type="email" autoComplete="email" className="auth-input"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'signin-email-error' : undefined}
                {...register('email')} />
              {errors.email && <span id="signin-email-error" className="auth-error" role="alert">{errors.email.message}</span>}
            </div>
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">Mot de passe</label>
              <input id="password" type="password" autoComplete="current-password" className="auth-input"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? 'signin-password-error' : undefined}
                {...register('password')} />
              {errors.password && <span id="signin-password-error" className="auth-error" role="alert">{errors.password.message}</span>}
            </div>
            <button type="submit" className="auth-button-primary" disabled={isSubmitting} data-testid="signin-submit">
              {isSubmitting ? 'Connexion…' : 'Se connecter'}
            </button>
            <p className="auth-help-text" style={{ textAlign: 'center' }}>
              <Link to="/forgot-password/" className="auth-link">Mot de passe oublié ?</Link>
            </p>
            <p className="auth-help-text" style={{ textAlign: 'center' }}>
              Première connexion ? <Link to="/signup/" className="auth-link">Créer un compte famille</Link>
            </p>
          </form>
        </div>
      </main>
    </Layout>
  );
}

export default SignIn;
